/**
 * Canonical URL utilities for SEO
 * Master domain: https://gistgift.shop
 */

export const CANONICAL_DOMAIN = 'https://gistgift.shop';

/**
 * Normalize a URL path for canonical use
 * - Removes trailing slashes (except for root)
 * - Ensures leading slash
 * - Removes duplicate slashes
 */
export function normalizeCanonicalPath(path: string): string {
  if (!path || path === '/') return '';
  
  // Remove duplicate slashes
  let normalized = path.replace(/\/+/g, '/');
  
  // Ensure leading slash
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }
  
  // Remove trailing slash
  normalized = normalized.replace(/\/+$/, '');
  
  return normalized;
}

/**
 * Build a full canonical URL from a path
 */
export function buildCanonicalUrl(path: string): string {
  const normalizedPath = normalizeCanonicalPath(path);
  return `${CANONICAL_DOMAIN}${normalizedPath}`;
}

/**
 * Check if current URL needs normalization (trailing slash removal, etc.)
 * Returns the normalized URL if redirect is needed, null otherwise
 */
export function getNormalizedUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  const { pathname, search, hash } = window.location;
  
  // Skip normalization for hash routes (native app)
  if (hash && hash.startsWith('#/')) return null;
  
  // Check for trailing slash (except root)
  if (pathname !== '/' && pathname.endsWith('/')) {
    const normalizedPath = pathname.replace(/\/+$/, '');
    return `${normalizedPath}${search}${hash}`;
  }
  
  return null;
}

/**
 * Pages that should have canonical tags (public-facing SEO pages)
 */
export const CANONICAL_PAGES = [
  '/',
  '/privacy-policy',
  '/terms-of-service',
  '/about',
  '/partners',
  '/ai-gift-finder',
  '/gift-ideas-by-personality',
  '/last-minute-gift-ideas',
  '/birthday-gift-ideas',
  '/anniversary-gift-ideas',
  '/valentines-day-gifts',
  '/christmas-gift-ideas',
  '/gifts-for-her',
  '/gifts-for-him',
  '/ai-gift-app',
  '/gift-from-photo',
  '/photo-gift-finder',
  '/gift-generator-iphone',
  '/personalized-gift-app',
  '/best-white-elephant-gifts',
  '/best-wedding-gifts',
  '/best-housewarming-gifts',
  '/best-birthday-gift-for-best-friend',
  '/best-engagement-gifts',
] as const;

/**
 * Check if a path should have a canonical tag
 */
export function shouldHaveCanonical(path: string): boolean {
  const normalizedPath = normalizeCanonicalPath(path) || '/';
  return CANONICAL_PAGES.includes(normalizedPath as typeof CANONICAL_PAGES[number]);
}
